import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'clsx';

import { noop } from '../../default-props/noop';
import { FormattedMessagePropType } from '../../../prop-types/intl';
import { TooltipPosition } from '../../prop-types/tooltip-position';
import { PopoverContent } from './popover-content';
import { PopoverContentChild } from './popover-content/child';
import { ContentBody } from './content-body';
import { ContentHeader } from './content-header';
import { POPOVER_FLYOUT, POPOVER_TOOLTIP } from './constants';
import { previewMessageDescriptor } from './messages';

import styles from './interactable-popover.module.less';
import { useToggle } from 'ui-components/hooks/use-toggle';

const defaultPreviewMessage = <FormattedMessage {...previewMessageDescriptor} />;

/*
  This component is comprised of 2 popover styles:
    1. flyout opened by clicking on target, with a close button
    2. tooltip (src\ui-components\components\tooltip\index.js)
  See FIGMA https://www.figma.com/file/pH8AQOEjZTgRr9QM0l6GNf/Error-Messaging
  FL - SPD-10778 tracks our effort to consolidate components
*/
const popoverContentType = (<PopoverContent />).type;

const createPopoverRootIfDoesNotExist = (containerId) => {
  if (!global.document.getElementById(containerId)) {
    const popoverWrapper = global.document.createElement('div');

    popoverWrapper.setAttribute('class', 'popover-top-level-wrapper');
    popoverWrapper.setAttribute('id', containerId);
    popoverWrapper.setAttribute('style', 'position:relative;');

    global.document.body.appendChild(popoverWrapper);
  }
};

export const InteractablePopover = ({
  children,
  popoverBody,
  title,
  preferPlace = 'top',
  hideTooltipTitle: hideTooltipTitleProp = false,
  ignoreTooltip = false,
  ignoreFlyout = false,
  openDelayMs = 0,
  useMessagesForTooltipText = false,
  containerId = 'popover-wrapper-container',
  previewMessage = defaultPreviewMessage,
  targetClassName = '',
  shouldBeDark: shouldBeDarkProp = false,
  onPopOverOpen = noop,
  contentRenderProp = noop,
  onHoverOpen = noop,
}) => {
  createPopoverRootIfDoesNotExist(containerId);

  const { isActivated: hasHover, deactivate: removeHover, activate: addHover } = useToggle(false);
  const { isActivated: isOpen, deactivate: close, activate: open } = useToggle(false);
  const [popoverType, setPopoverType] = useState(POPOVER_TOOLTIP);

  const ref = useRef();
  const popoverWrapperRef = useRef(null);
  const targetRef = useRef(null);

  const popoverClassname = `${popoverType}-popover`;
  const hideTooltipTitle = hideTooltipTitleProp || useMessagesForTooltipText;
  const shouldBeDark = typeof shouldBeDarkProp === 'function' ? shouldBeDarkProp(popoverType) : shouldBeDarkProp;

  const otherElement = React.Children.toArray(children).find((child) => child.type !== popoverContentType);

  const handleEscapeKeyPress = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        removeHover();
      }
    },
    [removeHover],
  );

  const handleOnClickOutside = useCallback(
    (e) => {
      const targetEl = e.target;
      setTimeout(() => {
        const currentPopoverEl = popoverWrapperRef.current;
        const currentTargetIconEl = targetRef.current;

        if (
          (currentTargetIconEl && currentTargetIconEl.matches(':hover')) ||
          (currentTargetIconEl && currentTargetIconEl.matches(':focus')) ||
          (currentTargetIconEl && currentTargetIconEl.contains(targetEl)) ||
          (currentPopoverEl && currentPopoverEl.contains(targetEl))
        ) {
          return;
        }

        removeHover();
      }, 50);
    },
    [removeHover],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleOnClickOutside, true);
      document.addEventListener('keydown', handleEscapeKeyPress, true);
    }

    return () => {
      document.removeEventListener('click', handleOnClickOutside, true);
      document.removeEventListener('keydown', handleEscapeKeyPress, true);
    };
  }, [isOpen, handleEscapeKeyPress, handleOnClickOutside]);

  useEffect(() => {
    if (!hasHover) {
      close();
    } else if (openDelayMs <= 0) {
      open();
    } else {
      const delayTimer = setTimeout(() => {
        open();
      }, openDelayMs);
      return () => {
        clearTimeout(delayTimer);
      };
    }
  }, [openDelayMs, hasHover, close, open]);

  const onClick = useCallback(
    (e) => {
      e.stopPropagation();
      setPopoverType(POPOVER_FLYOUT);
      addHover();
      onPopOverOpen();
    },
    [onPopOverOpen, addHover],
  );

  const handleFlyoutOnClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const onMouseEnter = useCallback(() => {
    if (popoverType === POPOVER_FLYOUT && isOpen) return;
    setPopoverType(POPOVER_TOOLTIP);
    addHover();
    onHoverOpen();
  }, [addHover, isOpen, onHoverOpen, popoverType]);

  const onMouseLeave = useCallback(() => {
    if (popoverType === POPOVER_FLYOUT && isOpen) return;
    removeHover();
  }, [isOpen, popoverType, removeHover]);

  const content = useMemo(() => {
    if (contentRenderProp !== noop) {
      return contentRenderProp({ close, popoverType });
    }
    return (
      <>
        <ContentHeader hideTooltipTitle={hideTooltipTitle} isDark={shouldBeDark} onClose={close} popoverType={popoverType} title={title} />
        <ContentBody
          ignoreFlyout={ignoreFlyout}
          isDark={shouldBeDark}
          popoverBody={popoverBody}
          popoverType={popoverType}
          previewMessage={previewMessage}
          useMessagesForTooltipText={useMessagesForTooltipText}
        />
      </>
    );
  }, [
    close,
    contentRenderProp,
    hideTooltipTitle,
    ignoreFlyout,
    shouldBeDark,
    popoverBody,
    popoverType,
    previewMessage,
    title,
    useMessagesForTooltipText,
  ]);

  return (
    <React.Fragment>
      <div
        ref={targetRef}
        className={classnames(styles['popover-target'], { [styles.clickable]: !ignoreFlyout }, targetClassName)}
        onClick={ignoreFlyout ? undefined : onClick}
        onDoubleClick={handleFlyoutOnClick}
        onMouseEnter={ignoreTooltip ? undefined : onMouseEnter}
        onMouseLeave={ignoreTooltip ? undefined : onMouseLeave}
      >
        {React.cloneElement(otherElement, {
          ref,
        })}
      </div>
      {isOpen && (
        <PopoverContentChild ref={ref} containerId={containerId} isInteractable={popoverType === POPOVER_FLYOUT} position={preferPlace}>
          <PopoverContent isDark={shouldBeDark} position={preferPlace}>
            <div
              ref={popoverWrapperRef}
              className={classnames(styles['popover-container'], styles[popoverClassname], { [styles.dark]: shouldBeDark })}
              onClick={handleFlyoutOnClick}
              onDoubleClick={handleFlyoutOnClick}
            >
              {content}
            </div>
          </PopoverContent>
        </PopoverContentChild>
      )}
    </React.Fragment>
  );
};

InteractablePopover.propTypes = {
  children: PropTypes.node.isRequired,
  popoverBody: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  title: PropTypes.oneOfType([FormattedMessagePropType, PropTypes.func]),
  preferPlace: TooltipPosition,
  hideTooltipTitle: PropTypes.bool,
  ignoreTooltip: PropTypes.bool,
  ignoreFlyout: PropTypes.bool,
  openDelayMs: PropTypes.number,
  useMessagesForTooltipText: PropTypes.bool,
  containerId: PropTypes.string,
  previewMessage: FormattedMessagePropType,
  targetClassName: PropTypes.string,
  shouldBeDark: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  onPopOverOpen: PropTypes.func,
  contentRenderProp: PropTypes.func,
  onHoverOpen: PropTypes.func,
};
